import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Globe2 } from "lucide-react";
import { GmpSeal, WhoGmpSeal, UsfdaSeal, FssaiSeal } from "@/components/CertSeals";

const CERTIFICATIONS = [
  {
    testid: "cert-gmp",
    title: "GMP Certified",
    desc: "Good Manufacturing Practices — batch-level quality control",
    Seal: GmpSeal,
  },
  {
    testid: "cert-who",
    title: "WHO-GMP Certified",
    desc: "World Health Organisation aligned pharmaceutical standards",
    Seal: WhoGmpSeal,
  },
  {
    testid: "cert-fda",
    title: "USFDA Manufacturing Unit",
    desc: "Made in a US Food & Drug Administration registered facility",
    Seal: UsfdaSeal,
  },
  {
    testid: "cert-fssai",
    title: "FSSAI Certified",
    desc: "Food Safety & Standards Authority of India compliant",
    Seal: FssaiSeal,
  },
];

export default function Footer() {
  return (
    <footer className="section-dark px-5 pb-10 pt-20 sm:px-8" data-testid="footer">
      <div className="mx-auto max-w-[1400px]">
        {/* CERTIFICATIONS & RESEARCH */}
        <section
          className="mb-16 rounded-2xl border border-white/10 bg-[radial-gradient(ellipse_at_top_left,rgba(52,216,160,0.08),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(47,128,237,0.08),transparent_55%)] p-8 sm:p-10"
          data-testid="certifications-section"
        >
          <div className="grid gap-10 lg:grid-cols-[1fr_2fr] lg:items-center">
            <div>
              <p className="font-mono text-xs uppercase tracking-overline text-[#34D8A0]">Trusted Compliance</p>
              <h3 className="mt-3 font-display text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl">
                Manufactured to the world&apos;s highest standards.
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-white/60">
                Every Physio Essential formula is produced in facilities held to international pharmaceutical and nutraceutical protocols — so what your physiotherapist recommends is exactly what reaches you.
              </p>

              {/* R&D credit */}
              <Link
                to="/about"
                className="mt-8 inline-flex flex-wrap items-center gap-3 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 backdrop-blur-sm transition-colors hover:border-[#34D8A0]/50 hover:bg-white/[0.08]"
                data-testid="rd-credit"
              >
                <Globe2 className="h-4 w-4 shrink-0 text-[#34D8A0]" />
                <span className="font-mono text-[11px] uppercase tracking-overline text-white/80">
                  Researched &amp; Developed at
                  <span className="ml-2 text-white">GD Global Health Ltd</span>
                  <span className="mx-2 text-white/30">·</span>
                  <span className="text-[#34D8A0]">New Zealand</span>
                </span>
                <ArrowUpRight className="h-3.5 w-3.5 text-white/60" />
              </Link>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {CERTIFICATIONS.map((c) => {
                const Seal = c.Seal;
                return (
                  <div
                    key={c.title}
                    className="group relative flex flex-col justify-between overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-[#34D8A0]/40 hover:bg-white/[0.06]"
                    data-testid={c.testid}
                  >
                    <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-[#34D8A0]/5 blur-2xl" />
                    <div className="relative mx-auto h-28 w-28">
                      <Seal />
                    </div>
                    <div className="relative mt-4 text-center">
                      <div className="font-display text-base font-bold leading-tight tracking-tight text-white">
                        {c.title}
                      </div>
                      <div className="mt-1.5 text-[11px] leading-relaxed text-white/50">{c.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <div className="grid gap-12 border-b border-white/10 pb-16 lg:grid-cols-[1.5fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-3">
              <img src="/logo-icon.png" alt="Physio Essential" className="h-11 w-11 object-contain" />
              <div className="leading-none">
                <div className="font-display text-2xl font-extrabold tracking-tight">
                  Physio<span className="font-medium text-[#34D8A0]"> essential</span>
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.24em] text-[#34D8A0]">Beyond Nutrition</div>
              </div>
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/50">
              Clinical-grade supplements engineered for orthopedic, neurological and everyday physiotherapy recovery.
              Formulated with practising physical therapists.
            </p>
          </div>

          <div>
            <p className="mb-4 font-mono text-xs uppercase tracking-overline text-white/40">Shop</p>
            <ul className="space-y-3 text-sm text-white/70">
              <li><Link className="link-underline" to="/shop?category=bone">Bone Health</Link></li>
              <li><Link className="link-underline" to="/shop?category=muscle">Muscle Recovery</Link></li>
              <li><Link className="link-underline" to="/shop?category=joint">Joint Health</Link></li>
              <li><Link className="link-underline" to="/shop?category=sports">Sports Recovery</Link></li>
              <li><Link className="link-underline" to="/shop?category=postsurgery">Post-Surgery</Link></li>
            </ul>
          </div>

          <div>
            <p className="mb-4 font-mono text-xs uppercase tracking-overline text-white/40">Company</p>
            <ul className="space-y-3 text-sm text-white/70">
              <li><Link className="link-underline" to="/about">Our Science</Link></li>
              <li><Link className="link-underline" to="/shop">All Products</Link></li>
              <li><Link className="link-underline" to="/orders">Track Order</Link></li>
              <li><Link className="link-underline" to="/wishlist">Wishlist</Link></li>
              <li><Link className="link-underline" to="/contact">Contact</Link></li>
            </ul>
          </div>

          <div>
            <p className="mb-4 font-mono text-xs uppercase tracking-overline text-white/40">Newsletter</p>
            <p className="mb-4 text-sm text-white/60">Recovery protocols in your inbox.</p>
            <div className="flex items-center border-b border-white/30 pb-2">
              <input
                placeholder="Email address"
                className="w-full bg-transparent text-sm text-white placeholder:text-white/40 focus:outline-none"
                data-testid="newsletter-input"
              />
              <ArrowUpRight className="h-4 w-4 text-[#34D8A0]" />
            </div>
          </div>
        </div>

        <div className="flex flex-col items-start justify-between gap-4 pt-8 text-xs text-white/40 sm:flex-row sm:items-center">
          <span className="font-mono">© {new Date().getFullYear()} PHYSIO ESSENTIAL — BEYOND NUTRITION</span>
          <span className="font-mono uppercase tracking-overline">Payments secured by Razorpay · UPI · GPay · Paytm</span>
        </div>
      </div>
    </footer>
  );
}
